import _, { isNull, isUndefined } from 'lodash'
import { Box } from '@chakra-ui/layout'
import { Accordion, Button, Center, Spinner } from '@chakra-ui/react'
import type { FormikProps } from 'formik'
import { useFormik } from 'formik'
import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import {
  MediaLibrarySettingsForm,
  CustomMetadataSettingComponent,
  SavedExtensionsSetting,
  DeleteMediaLibrary,
} from '../../components/Settings'
import useToast from '../../hooks/useToast'
import { MainContentHeadingBox } from '../Base/MainContent'
import { UserContext } from '../../providers'
import CustomPlanCTA from '../../components/CustomPlan'
import { FeaturesEnum } from '../../../utils/features'
import PermissionGuard from '../../components/PermissionGuard'
import { PermissionsEnum } from '../../../utils'
import type { MediaLibrarySettings, SettingsResponse } from '../../interfaces'
import ClientTagsSettings from '../../components/Settings/ClientTags'
import { CustomMetadataContext } from '../../providers/CustomMetadataProvider'
import {
  AutoTaggingObj,
  ExtensionData,
  Extensions,
  RemoveBgProps,
} from '../../interfaces/SettingsResponse'
import * as Yup from 'yup'
import debounce from 'lodash/debounce'
import {
  getCmFieldValidatorMap,
  validateCmField,
} from '../../components/MediaLibrary/NewButton/uploadUtils'
import PublicLinksSettingsComponent from '../../components/Settings/PublicLinks'

const MediaLibrarySettingsLayout = (props: {
  form?: FormikProps<SettingsResponse>
}): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { cmFields = [] } = useContext(CustomMetadataContext)
  const [isInitialRender, setInitialRender] = React.useState(true)
  const { user, refetch: refetchUser } = useContext(UserContext)
  const { patch, response: formResponse } = useFetch()
  const [settingsResponse, setSettingsResponse] = React.useState<MediaLibrarySettings>()

  const handleError = (): void => {
    toast({
      description: t(
        'something_went_wrong_try_again',
        'Something went wrong, please try again after sometime',
      ),
      status: 'error',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })
  }

  const validationSchema = Yup.object().shape({
    upload: Yup.object().shape({
      ['customCoordinatesOb']: Yup.object()
        .shape({
          ['x-coordinate']: Yup.number().typeError(
            'x-coordinate must be a number',
          ),
          ['y-coordinate']: Yup.number().typeError(
            'y-coordinate must be a number',
          ),
          width: Yup.number().typeError('Width must be a number'),
          height: Yup.number().typeError('Height must be a number'),
        })
        .optional(),
    }),
  })

  const updatedCmField = React.useMemo(() => {
    return cmFields?.map(data => ({
      ...data,
      name: `IK_TEMP_CUSTOM_METADATA_${data.name}`,
      originalName: data.name,
    }))
  }, [cmFields])

  const cmFieldValidatorMap = getCmFieldValidatorMap(updatedCmField)

  const detectCmFields = React.useCallback(
    values => {
      const cmFieldsName = updatedCmField.map(data => data.name)
      const customMetadata = Object.keys(values)
        .filter(fieldName => cmFieldsName?.includes(fieldName))
        .reduce((ob, data) => ({ ...ob, [data]: values[data] }), {})
      const tempCustomMetadata = {}
      Object.keys(customMetadata).forEach(key => {
        if (
          key.split('IK_TEMP_CUSTOM_METADATA_')?.length &&
          key.split('IK_TEMP_CUSTOM_METADATA_')[1]
        )
          tempCustomMetadata[key.split('IK_TEMP_CUSTOM_METADATA_')[1]] =
            customMetadata[key]
      })
      return tempCustomMetadata
    },
    [updatedCmField],
  )

  const onSubmit = React.useCallback(
    values => {
      let submitData = {}
      if (Object.keys(values)?.length) {
        if (values?.tags && Array.isArray(values.tags))
          submitData['tags'] = values?.tags?.map(data => data.label)
        if (
          detectCmFields(values) &&
          Object.keys(detectCmFields(values))?.length
        ) {
          const cmOb = detectCmFields(values)
          submitData['customMetadata'] = Object.keys(cmOb).map(key => ({
            [key]: cmOb[key],
          }))
        }
        if (
          (values &&
            values?.customCoordinatesOb?.['x-coordinate'] &&
            values?.customCoordinatesOb?.['y-coordinate'] &&
            values?.customCoordinatesOb?.height &&
            values?.customCoordinatesOb.width) ||
          !(
            values &&
            values?.customCoordinatesOb?.['x-coordinate'] &&
            values?.customCoordinatesOb?.['y-coordinate'] &&
            values?.customCoordinatesOb?.height &&
            values?.customCoordinatesOb.width
          )
        )
          submitData['customCoordinates'] = {
            ['x-coordinate']: Number(
              values.customCoordinatesOb?.['x-coordinate'],
            ),
            ['y-coordinate']: Number(
              values.customCoordinatesOb?.['y-coordinate'],
            ),
            width: Number(values.customCoordinatesOb?.width),
            height: Number(values.customCoordinatesOb?.height),
          }

        if (values && values?.extensionsData) {
          let tempExt: Extensions[] = []
          if (values?.extensionsData?.aiTag) {
            tempExt = [...values?.aiTagData]
          }
          if (values?.extensionsData?.removeBg) {
            const options = {
              add_shadow: values?.removeBgData?.shadowCheck,
              semitransparency: values?.removeBgData?.semiTransparentCheck,
            }
            if (
              values?.removeBgData?.background === 'background_color' &&
              values?.removeBgData?.bgColor
            )
              options['bg_color'] = values?.removeBgData?.bgColor
            if (
              values?.removeBgData?.background === 'background_image' &&
              values?.removeBgData?.bgImageURL
            )
              options['bg_image_url'] = values?.removeBgData?.bgImageURL
            tempExt.push({
              name: 'remove-bg',
              options,
            })
          }
          if (values?.extensionsData?.autoDescription){
            tempExt.push({
              name: 'ai-auto-description'
            })
          }
          submitData['extensions'] = tempExt
        }

        // Handle saved extensions - always send if the field exists, even if empty
        if (values && Array.isArray(values?.savedExtensions)) {
          submitData['savedExtensions'] = values.savedExtensions
        }

        if (values && Object.keys(values)?.includes('webhookUrl'))
          submitData['webhookUrl'] = values?.webhookUrl

        if (values && Object.keys(values)?.includes('uniqueName'))
          submitData['uniqueName'] = values?.uniqueName.toString()

        if (values && Object.keys(values)?.includes('overwriteFile'))
          submitData['overwriteFile'] = values?.overwriteFile.toString()

        if (values && Object.keys(values)?.includes('isPrivateFile'))
          submitData['isPrivateFile'] = values?.isPrivateFile.toString()
        
        if (values && Object.keys(values)?.includes('publishFile')) {
          submitData['publishFile'] = values?.publishFile.toString()
        }

        if (values && Object.keys(values)?.includes('overwriteAITags'))
          submitData['overwriteAITags'] = values?.overwriteAITags.toString()

        if (values && Object.keys(values)?.includes('overwriteTags'))
          submitData['overwriteTags'] = values?.overwriteTags.toString()

        if (values && Object.keys(values)?.includes('overwriteDescription'))
          submitData['overwriteDescription'] = values?.overwriteDescription.toString()

        if (values && Object.keys(values)?.includes('overwriteCustomMetadata'))
          submitData['overwriteCustomMetadata'] =
            values?.overwriteCustomMetadata.toString()
            
        if (values && Object.keys(values)?.includes('preTransformation')) {
          submitData['preTransformation'] = values?.preTransformation.toString()
        }
        if (values && Object.keys(values)?.includes('postTransformation')) {
          submitData['postTransformation'] = isNull(values.postTransformation)
            ? []
            : values?.postTransformation
        }
        return submitData
      }
    },
    [detectCmFields],
  )

  const mlForm = useFormik<MediaLibrarySettings>({
    initialValues: {
      upload: {
        uniqueName: 'false',
      },
    },
    onSubmit: async (values, formikHelper) => {
      if (!user) {
        return
      }
      await patch(
        `/api/clients/${user.clientNumber}/settings/v2/media-library`,
        { upload: onSubmit(values?.upload) },
      )
      if (!formResponse.ok) {
        return void handleError()
      }
      toast({
        description: t(
          'successfully_saved_settings',
          'Successfully saved settings',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      formikHelper.resetForm({ values })
      void refetchUser()
    },
    validateOnChange: false,
    validate: values => {
      return validateCmField(
        values?.upload,
        updatedCmField,
        cmFieldValidatorMap,
        true,
      )
    },
    validationSchema,
  })

  const { request, response, loading } = useFetch<MediaLibrarySettings>({
    cachePolicy: CachePolicies.NO_CACHE,
    loading: Boolean(props.form),
  })

  const form = props.form ?? mlForm

  const getInitialValues = React.useCallback(
    values => {
      let fileObject

      if (values?.upload?.tags && Array.isArray(values?.upload?.tags)) {
        fileObject = {
          ...fileObject,
          tags: values?.upload?.tags?.map((data: string) => ({
            label: data,
            value: data,
          })),
        }
      }
      if (values?.upload?.extensions?.length) {
        const aiTagData: AutoTaggingObj[] = []
        let removeBgData: RemoveBgProps
        const extensionsData: ExtensionData = {}
        values?.upload?.extensions?.forEach((ext: Extensions, index) => {
          if (
            ext?.name === 'aws-auto-tagging' ||
            ext?.name === 'google-auto-tagging'
          )
            aiTagData.push(ext)

          if (ext?.name === 'remove-bg') {
            removeBgData = {
              background: ext?.options?.bg_color
                ? 'background_color'
                : ext?.options?.bg_image_url
                ? 'background_image'
                : 'no_background',
              bgColor: ext?.options?.bg_color
                ? ext?.options?.bg_color
                : '#0450d5',
              bgImageURL: ext?.options?.bg_image_url
                ? ext?.options?.bg_image_url
                : '',
              semiTransparentCheck: ext?.options?.semitransparency ?? false,
              shadowCheck: ext?.options?.add_shadow ?? false,
            }
            fileObject = { ...fileObject, removeBgData }
            extensionsData.removeBg = true
          }

          if (ext?.name === "ai-auto-description"){
            extensionsData.autoDescription = true
          }
        })
        if (aiTagData?.length) {
          fileObject = { ...fileObject, aiTagData }
          extensionsData.aiTag = true
        }
        fileObject = { ...fileObject, extensionsData }
      }
      fileObject = {
        ...fileObject,
        savedExtensions: (values?.upload?.savedExtensions && Array.isArray(values?.upload?.savedExtensions)) 
          ? values?.upload?.savedExtensions 
          : [],
      }
      if (values?.upload?.tags && Array.isArray(values?.upload?.tags)) {
        fileObject = {
          ...fileObject,
          tags: values?.upload?.tags?.map((data: string) => ({
            label: data,
            value: data,
          })),
        }
      }
      if (
        values?.upload?.customCoordinates &&
        Object.keys(values?.upload?.customCoordinates)?.length &&
        !Object.keys(values?.upload?.customCoordinates)?.every(
          key => values?.upload?.customCoordinates[key] === 0,
        )
      ) {
        fileObject = {
          ...fileObject,
          customCoordinatesOb: values?.upload?.customCoordinates,
        }
      }
      if (values?.upload?.webhookUrl) {
        fileObject = {
          ...fileObject,
          webhookUrl: values?.upload?.webhookUrl,
        }
      }
      if (
        values?.upload?.customMetadata &&
        Array.isArray(values?.upload?.customMetadata)
      ) {
        const customMetadata = values?.upload?.customMetadata?.reduce(
          (ob, data) => ({ ...ob, ...data  }),
          {},
        )
        const tempCustomMetadata = {}
        Object.keys(customMetadata).forEach(key => {
          tempCustomMetadata[`IK_TEMP_CUSTOM_METADATA_${key}`] =
            customMetadata[key]
        })
        fileObject= { ...fileObject, ...tempCustomMetadata }
      }
      if (updatedCmField?.length) {
        updatedCmField.forEach(cmData => {
          if (
            !(
              isUndefined(cmData?.schema?.defaultValue) ||
              isNull(cmData?.schema?.defaultValue)
            ) &&
            ((fileObject &&
              (isUndefined(fileObject[cmData?.name]) ||
                isNull(fileObject[cmData?.name]))) ||
              !fileObject)
          ) {
            fileObject = {
              ...fileObject,
              [cmData?.name]: cmData?.schema?.defaultValue,
            }
          }
        })
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('uniqueName')
      ) {
        fileObject = {
          ...fileObject,
          uniqueName: values?.upload?.uniqueName === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('isPrivateFile')
      ) {
        fileObject = {
          ...fileObject,
          isPrivateFile:
            values?.upload?.isPrivateFile === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('publishFile')
      ) {
        fileObject = {
          ...fileObject,
          publishFile:
            values?.upload?.publishFile === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('overwriteFile')
      ) {
        fileObject = {
          ...fileObject,
          overwriteFile:
            values?.upload?.overwriteFile === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('overwriteTags')
      ) {
        fileObject = {
          ...fileObject,
          overwriteTags:
            values?.upload?.overwriteTags === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('overwriteDescription')
      ) {
        fileObject = {
          ...fileObject,
          overwriteDescription:
            values?.upload?.overwriteDescription === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('overwriteAITags')
      ) {
        fileObject = {
          ...fileObject,
          overwriteAITags:
            values?.upload?.overwriteAITags === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('overwriteCustomMetadata')
      ) {
        fileObject = {
          ...fileObject,
          overwriteCustomMetadata:
            values?.upload?.overwriteCustomMetadata === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('preTransformation')
      ) {
        fileObject = {...fileObject, preTransformation: values?.upload?.preTransformation,}
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('postTransformation')
      ) {
        fileObject = {...fileObject, postTransformation: values?.upload?.postTransformation,}
      }
      return fileObject
    },
    [updatedCmField],
  )

  const debouncedValidate = React.useMemo(
    () => debounce(form.validateForm, 500),
    [form.validateForm],
  )

  useEffect(() => {
    debouncedValidate(form.values)
  }, [form.values, debouncedValidate])

  React.useEffect(() => {
    if (user?.clientNumber && _.isUndefined(props.form)) {
      void request
        .get(`/api/clients/${user.clientNumber}/settings/media-library`)
        .then(values => {
          if (!response.ok) {
            return void handleError()
          }
          setSettingsResponse(values)
          if (isInitialRender) setInitialRender(false)
        })
    }
  }, [])

  useEffect(() => {
    if (settingsResponse && mlForm)
      mlForm.resetForm({
        values: {
          upload: getInitialValues({
            upload: {
              ...settingsResponse?.upload,
              customMetadata: user?.customMetadata,
            },
          }),
        },
      })
  }, [settingsResponse, cmFields, user])

  const { dirty, submitForm, isSubmitting } = form

  if (_.isUndefined(props.form) && loading && !isInitialRender) {
    return (
      <Center height="100%">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="appPaleGrey"
          color="appBlue"
          size="xl"
        />
      </Center>
    )
  }
  return (
    <>
      <MainContentHeadingBox reverse={true}>
        <PermissionGuard permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}>
          {(hasPermission: boolean) =>
            hasPermission ? (
              user?.pricingPlan.featuresList.includes(
                FeaturesEnum.MEDIA_LIBRARY_STORAGE,
              ) ? (
                <Button
                  isDisabled={!dirty}
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('save')}
                </Button>
              ) : (
                <></>
              )
            ) : (
              <></>
            )
          }
        </PermissionGuard>
      </MainContentHeadingBox>

      <Box>
        {user?.pricingPlan.featuresList.includes(
          FeaturesEnum.MEDIA_LIBRARY_STORAGE,
        ) ? (
          <>
            <Accordion borderColor="#9ea3ad33" allowToggle>
              <MediaLibrarySettingsForm form={form} />
              <CustomMetadataSettingComponent />
              <SavedExtensionsSetting />
              <PermissionGuard
                permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}
              >
                {(hasPermission: boolean) =>
                  hasPermission ? <ClientTagsSettings /> : <></>
                }
              </PermissionGuard>
              <PermissionGuard
                permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}
              >
                {(hasPermission: boolean) => (
                  (
                    hasPermission &&
                    user?.pricingPlan.featuresList.includes(
                      FeaturesEnum.PUBLIC_LINKS,
                    )
                  ) ? (
                    <PublicLinksSettingsComponent />
                  ): <></>
                )
                }
              </PermissionGuard>
              <DeleteMediaLibrary />
            </Accordion>
          </>
        ) : (
          <CustomPlanCTA feature={FeaturesEnum.MEDIA_LIBRARY_STORAGE} />
        )}
      </Box>
    </>
  )
}

export default MediaLibrarySettingsLayout
